import * as d3 from 'd3';
import { CONFIG } from './config';

// =============================================================================
// DATA LOADING
// =============================================================================

export async function loadData() {
  try {
    const [hierarchy, clusterEdges, unifiedEdges, registry, metadata] = await Promise.all([
      fetch('/data/hierarchy.json').then(r => r.json()),
      fetch('/data/cluster_edges.json').then(r => r.json()),
      fetch('/data/unified_edges_enriched.json').then(r => r.json()),
      fetch('/data/unified_registry.json').then(r => r.json()),
      fetch('/data/metadata.json').then(r => r.json())
    ]);
    
    // Build lookup maps for efficient paper resolution
    const unifiedEdgesMap = {};
    (unifiedEdges.unified_edges || []).forEach(edge => {
      unifiedEdgesMap[edge.unified_edge_id] = edge;
    });
    
    const papersMap = {};
    (metadata.papers || []).forEach(paper => {
      // Index by various ID formats that might appear in paper_id
      papersMap[paper.id] = paper;
      if (paper.pmcid) papersMap[paper.pmcid] = paper;
      // Also index by the full paper_id format (e.g., "PMC12642786_Title...")
      // The paper_id in evidence often has the title appended
    });
    
    return { 
      hierarchy, 
      clusterEdges, 
      unifiedEdges, 
      registry, 
      metadata,
      // Lookup maps for efficient access
      unifiedEdgesMap,
      papersMap
    };
  } catch (error) {
    console.error('Error loading data:', error);
    return null;
  }
}


// =============================================================================
// CLUSTER HELPERS
// =============================================================================

export function buildClusterLookup(hierarchy) {
  const lookup = {};
  
  // First pass: build lookup with parent_id (not parent reference)
  function traverse(node, parentId = null) {
    lookup[node.cluster_id] = { ...node, parent_id: parentId };
    if (node.children) {
      node.children.forEach(child => traverse(child, node.cluster_id));
    }
  }
  
  traverse(hierarchy.root);
  
  // Second pass: resolve parent references to actual lookup entries
  Object.values(lookup).forEach(entry => {
    if (entry.parent_id) {
      entry.parent = lookup[entry.parent_id];
    } else {
      entry.parent = null;
    }
  });
  
  return lookup;
}

/**
 * Resolve underlying papers for a cluster edge.
 * Returns array of { title, doi, url, pmcid, year, authors } objects.
 */
export function getUnderlyingPapers(clusterEdge, unifiedEdgesMap, papersMap) {
  if (!clusterEdge?.underlying_unified_edges) return [];
  
  const papers = new Map(); // Use Map to deduplicate by paper ID
  
  for (const unifiedEdgeId of clusterEdge.underlying_unified_edges) {
    const unifiedEdge = unifiedEdgesMap[unifiedEdgeId];
    if (!unifiedEdge?.evidence) continue;
    
    for (const evidence of unifiedEdge.evidence) {
      const paperId = evidence.paper_id;
      if (!paperId || papers.has(paperId)) continue;
      
      // Try to find the paper in metadata
      // paper_id might be "PMC12642786_Title..." or just "PMC12642786"
      const pmcidMatch = paperId.match(/^(PMC\d+)/);
      const pmcid = pmcidMatch ? pmcidMatch[1] : paperId;
      
      const paperMeta = papersMap[pmcid] || papersMap[paperId];
      
      if (paperMeta) {
        papers.set(paperId, {
          id: pmcid,
          title: paperMeta.title || evidence.paper_title || 'Unknown title',
          doi: paperMeta.doi,
          url: paperMeta.doi 
            ? `https://doi.org/${paperMeta.doi}` 
            : paperMeta.pdf_url || `https://www.ncbi.nlm.nih.gov/pmc/articles/${pmcid}/`,
          pmcid: paperMeta.pmcid || pmcid,
          year: paperMeta.year,
          authors: paperMeta.authors || []
        });
      } else {
        // Fallback: create entry from evidence data
        papers.set(paperId, {
          id: pmcid,
          title: evidence.paper_title || paperId,
          doi: null,
          url: `https://www.ncbi.nlm.nih.gov/pmc/articles/${pmcid}/`,
          pmcid: pmcid,
          year: null,
          authors: []
        });
      }
    }
  }
  
  // Convert to array and sort by year (newest first), then by title
  return Array.from(papers.values()).sort((a, b) => {
    if (a.year && b.year) return b.year - a.year;
    if (a.year) return -1;
    if (b.year) return 1;
    return a.title.localeCompare(b.title);
  });
}

export function getClustersAtLevel(hierarchy, level) {
  const clusters = [];
  
  function traverse(node) {
    if (node.level === level) {
      clusters.push(node);
    } else if (node.children) {
      node.children.forEach(traverse);
    }
  }
  
  traverse(hierarchy.root);
  return clusters;
}

export function isOutcomeCluster(cluster, config = CONFIG) {
  if (config.manualOutcomeClusterIds.length > 0) {
    return config.manualOutcomeClusterIds.includes(cluster.cluster_id);
  }
  
  const nameLower = cluster.name.toLowerCase();
  return config.outcomePatterns.some(pattern => nameLower.includes(pattern));
}

export function getPrimaryMemberCount(cluster) {
  let count = 0;
  
  function traverse(node) {
    if (node.members) {
      count += node.members.filter(m => m.membership === 'primary').length;
    }
    if (node.children) {
      node.children.forEach(traverse);
    }
  }
  
  traverse(cluster);
  return count;
}

export function getAllDescendantIds(cluster) {
  const ids = new Set([cluster.cluster_id]);
  
  function traverse(node) {
    if (node.children) {
      node.children.forEach(child => {
        ids.add(child.cluster_id);
        traverse(child);
      });
    }
  }
  
  traverse(cluster);
  return ids;
}


// =============================================================================
// EDGE HELPERS
// =============================================================================

/**
 * Get cluster edges where both source and target are at the specified level.
 * Updated to support new format with hierarchy_level_source/target.
 */
export function getClusterEdgesAtLevel(clusterEdges, level) {
  return clusterEdges.cluster_edges.filter(e => {
    // Support both old format (hierarchy_level) and new format (hierarchy_level_source/target)
    if (e.hierarchy_level !== undefined) {
      return e.hierarchy_level === level;
    }
    return e.hierarchy_level_source === level && e.hierarchy_level_target === level;
  });
}

/**
 * Get cluster edges where source OR target (or both) are at the specified level.
 * Useful for finding all edges involving a particular level.
 */
export function getClusterEdgesInvolvingLevel(clusterEdges, level) {
  return clusterEdges.cluster_edges.filter(e => {
    if (e.hierarchy_level !== undefined) {
      return e.hierarchy_level === level;
    }
    return e.hierarchy_level_source === level || e.hierarchy_level_target === level;
  });
}

export function getDominantClinicalInterpretation(histograms) {
  if (!histograms || !histograms.clinical_interpretation) {
    return 'unknown';
  }
  
  const categories = [
    'very_beneficial', 'beneficial', 'neutral', 'harmful', 'very_harmful', 'not_applicable'
  ];
  
  const counts = histograms.clinical_interpretation;
  let maxIdx = 0;
  let maxCount = counts[0] || 0;
  
  for (let i = 1; i < counts.length; i++) {
    if ((counts[i] || 0) > maxCount) {
      maxCount = counts[i] || 0;
      maxIdx = i;
    }
  }
  
  return categories[maxIdx] || 'unknown';
}

export function getConnectedClusterIds(edges, clusterId) {
  const connected = new Set();
  connected.add(clusterId);
  
  edges.forEach(edge => {
    if (edge.source_cluster === clusterId) {
      connected.add(edge.target_cluster);
    }
    if (edge.target_cluster === clusterId) {
      connected.add(edge.source_cluster);
    }
  });
  
  return connected;
}

/**
 * Find edges to display based on which clusters are expanded.
 * 
 * SIMPLIFIED VERSION: Now that we have cross-level edges in the data,
 * we simply show edges where both source and target clusters are currently visible
 * AND are "leaf-visible" (not expanded, so edges go to them not their children).
 */
export function findEdgesForExpandedView(clusterEdges, expandedClusters, baseLevel, clusterLookup) {
  const allEdges = clusterEdges.cluster_edges;
  const result = [];
  const expandedSet = new Set(expandedClusters);
  
  // A cluster is visible if:
  // 1. It's at or above baseLevel, OR
  // 2. Its parent is visible AND expanded
  const isVisible = (clusterId) => {
    const cluster = clusterLookup[clusterId];
    if (!cluster) return false;
    
    // Level 0 (root) is never shown directly
    if (cluster.level === 0) return false;
    
    // Base level clusters are always visible
    if (cluster.level <= baseLevel) return true;
    
    // Deeper clusters are visible if parent is visible AND expanded
    if (cluster.parent) {
      const parentId = cluster.parent.cluster_id;
      return isVisible(parentId) && expandedSet.has(parentId);
    }
    
    return false;
  };
  
  // A cluster is a valid edge endpoint if it's visible AND NOT expanded
  // (if it's expanded, edges should go to its children instead)
  const isValidEndpoint = (clusterId) => {
    if (!isVisible(clusterId)) return false;
    
    // If this cluster is expanded, it's not a valid endpoint
    // (edges should connect to its children instead)
    if (expandedSet.has(clusterId)) return false;
    
    return true;
  };
  
  // Build set of valid endpoint cluster IDs
  const validEndpoints = new Set();
  Object.keys(clusterLookup).forEach(clusterId => {
    if (isValidEndpoint(clusterId)) {
      validEndpoints.add(clusterId);
    }
  });
  
  // Debug logging
  const endpointsByLevel = {};
  validEndpoints.forEach(id => {
    const level = clusterLookup[id]?.level ?? -1;
    endpointsByLevel[level] = (endpointsByLevel[level] || 0) + 1;
  });
  console.log('Valid edge endpoints by level:', endpointsByLevel);
  console.log('Expanded clusters:', [...expandedSet].map(id => clusterLookup[id]?.name + ' (L' + clusterLookup[id]?.level + ')'));
  
  // Simple filter: show edge if both endpoints are valid
  let debugCounts = { total: 0, sourceInvalid: 0, targetInvalid: 0, included: 0 };
  
  allEdges.forEach(edge => {
    debugCounts.total++;
    
    const sourceValid = validEndpoints.has(edge.source_cluster);
    const targetValid = validEndpoints.has(edge.target_cluster);
    
    if (!sourceValid) {
      debugCounts.sourceInvalid++;
      return;
    }
    if (!targetValid) {
      debugCounts.targetInvalid++;
      return;
    }
    
    debugCounts.included++;
    result.push({
      ...edge,
      // For compatibility with existing rendering code
      displaySource: edge.source_cluster,
      displayTarget: edge.target_cluster,
      originalSource: edge.source_cluster,
      originalTarget: edge.target_cluster
    });
  });
  
  console.log('Edge filtering (simplified):', debugCounts);
  
  return result;
}

export function getConnectedToFocusedCluster(allEdges, focusedClusterId, clusterLookup) {
  if (!focusedClusterId) return null;
  
  const connected = new Set();
  connected.add(focusedClusterId);
  
  // Add ancestors
  let current = clusterLookup[focusedClusterId];
  while (current) {
    connected.add(current.cluster_id);
    current = current.parent;
  }
  
  // Find edges that directly connect to the focused cluster
  allEdges.forEach(edge => {
    if (edge.source_cluster === focusedClusterId) {
      connected.add(edge.target_cluster);
      let ancestor = clusterLookup[edge.target_cluster];
      while (ancestor) {
        connected.add(ancestor.cluster_id);
        ancestor = ancestor.parent;
      }
    }
    if (edge.target_cluster === focusedClusterId) {
      connected.add(edge.source_cluster);
      let ancestor = clusterLookup[edge.source_cluster];
      while (ancestor) {
        connected.add(ancestor.cluster_id);
        ancestor = ancestor.parent;
      }
    }
  });
  
  return connected;
}


// =============================================================================
// CIRCLE PACKING
// =============================================================================

export function calculateChildCircleLayout(parentRadius, children, contentScale = 1.0) {
  if (!children || children.length === 0) return { childRadii: [], positions: [] };
  
  // Calculate member counts for each child
  const memberCounts = children.map(child => getPrimaryMemberCount(child));
  
  // Create hierarchy data for d3.pack
  const hierarchyData = {
    name: 'root',
    children: children.map((child, i) => ({
      name: child.name,
      value: memberCounts[i] || 1,
      index: i
    }))
  };
  
  // Create hierarchy and apply pack layout
  const hierarchy = d3.hierarchy(hierarchyData)
    .sum(d => d.value)
    .sort((a, b) => b.value - a.value);
  
  // Pack with padding between circles
  // Use 1.9 * parentRadius to fill more of the parent circle
  // Increase padding to give room for expansion at deeper levels
  const packSize = parentRadius * 2.05;
  const packPadding = 8 * contentScale;  // Increased from 3 to 8 for more spacing
  
  const packLayout = d3.pack()
    .size([packSize, packSize])
    .padding(packPadding);
  
  const packed = packLayout(hierarchy);
  const childNodes = packed.children || [];
  const centerOffset = packSize / 2;
  
  // Create a map from original index to packed node
  const nodeMap = new Map();
  childNodes.forEach(node => {
    nodeMap.set(node.data.index, node);
  });
  
  // Extract in original order - apply contentScale to min/max radii
  const positions = [];
  const childRadii = [];
  
  const minChildRadius = CONFIG.minChildRadius * contentScale;
  const maxChildRadius = CONFIG.maxChildRadius * contentScale;
  
  for (let i = 0; i < children.length; i++) {
    const node = nodeMap.get(i);
    if (node) {
      positions.push({
        x: node.x - centerOffset,
        y: node.y - centerOffset
      });
      const radius = Math.max(minChildRadius, Math.min(maxChildRadius, node.r));
      childRadii.push(radius);
    } else {
      positions.push({ x: 0, y: 0 });
      childRadii.push(minChildRadius);
    }
  }
  
  return { childRadii, positions };
}
